import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiArrowLeft, FiUser, FiCheck, FiZap } from 'react-icons/fi';
import { authenticatedFetch } from '../utils/api';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const HeaderBar = styled.div`
  background: transparent;
  border-bottom: none;
  padding: 16px 32px;
  box-shadow: none;
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  min-height: 60px;
  gap: 16px;
  z-index: 1;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 6px;
  color: white;
  padding: 6px 16px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  white-space: nowrap;
  grid-column: 1;
  min-width: fit-content;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

const PortalTitle = styled.h1`
  color: white;
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
  white-space: nowrap;
  grid-column: 2;
  justify-self: center;
  text-align: center;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const LightningBoltIcon = styled.img`
  height: 1.28em;
  width: auto;
  display: inline-block;
  vertical-align: middle;
  align-self: center;
  margin: 0;
`;

const MainContent = styled.div`
  flex: 1;
  padding: 24px;
  display: flex;
  justify-content: center;
  overflow-y: auto;
  position: relative;
  z-index: 1;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 800px;
`;

const SectionsContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
`;

const HeaderIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
`;

const PageTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
`;

const UserInfo = styled.div`
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserIcon = styled.div`
  width: 40px;
  height: 40px;
  background: #f8fafc;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  font-size: 1.2rem;
`;

const UserDetails = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
`;

const UserRole = styled.div`
  font-size: 0.95rem;
  color: #64748b;
`;

const AccessSection = styled.div`
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex: 1;
  min-width: 0;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 12px 0;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid ${props => props.enabled ? '#10b981' : '#e2e8f0'};
  transition: all 0.2s ease;
`;

const FeatureInfo = styled.div`
  flex: 1;
`;

const FeatureName = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
`;

const FeatureDescription = styled.div`
  font-size: 0.75rem;
  color: #64748b;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
  margin-left: 20px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: #10b981;
  }

  &:checked + span:before {
    transform: translateX(26px);
  }

  &:focus + span {
    box-shadow: 0 0 1px #10b981;
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 34px;

  &:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }
`;

const SaveButton = styled.button`
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  color: #333333;
  padding: 10px 20px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 30px;
  transition: background-color 0.2s ease;

  &:hover {
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const InteractiveLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  background: transparent;
  backdrop-filter: none;
  border-radius: 0;
  box-shadow: none;
  min-height: 400px;
  border: none;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 24px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  margin-bottom: 8px;
  text-align: center;
  letter-spacing: 0.3px;
`;

const LoadingProgress = () => {
  return (
    <InteractiveLoadingContainer>
      <LoadingSpinner />
      <LoadingText>Loading Permissions</LoadingText>
    </InteractiveLoadingContainer>
  );
};

const ErrorMessage = styled.div`
  text-align: center;
  color: #ef4444;
  padding: 40px;
  font-size: 1.1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const features = [
  { key: 'forecast', name: 'Forecast (Live)', description: 'Access to live forecast data and weather information' },
  { key: 'pdf', name: 'PDF Reports', description: 'View and download PDF reports' },
  { key: 'csv', name: 'CSV Reports', description: 'View and download CSV reports' },
  { key: 'downtime', name: 'Report Downtime', description: 'Report and manage downtime records' },
  { key: 'plant-details', name: 'Plant Details', description: 'View and edit plant information' },
];

const liveDataFeatures = [
  { key: 'live-day-ahead-forecast', name: 'Day-Ahead Forecast', description: 'Show day-ahead forecast data on live page' },
  { key: 'live-intraday-forecast', name: 'Intraday Forecast (Beta)', description: 'Show intraday forecast data on live page' },
  { key: 'live-production', name: 'Production', description: 'Show production data on live page' },
  { key: 'live-irradiance', name: 'Irradiance', description: 'Show irradiance data on live page' },
  { key: 'live-temperature', name: 'Temperature', description: 'Show temperature data on live page' },
];

const SetAccessPage = ({ user, onBack }) => {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPermissions();
  }, [user]);

  const fetchPermissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`/api/admin/users/${user.username}/permissions`);
      const data = await response.json();
      
      if (data.success) {
        // Initialize all features with default permissions (all enabled by default)
        const defaultPermissions = {};
        features.forEach(feature => {
          defaultPermissions[feature.key] = data.permissions?.[feature.key] !== false;
        });
        liveDataFeatures.forEach(feature => {
          defaultPermissions[feature.key] = data.permissions?.[feature.key] !== false;
        });
        setPermissions(defaultPermissions);
      } else {
        // If no permissions found, default to all enabled
        const defaultPermissions = {};
        features.forEach(feature => {
          defaultPermissions[feature.key] = true;
        });
        liveDataFeatures.forEach(feature => {
          defaultPermissions[feature.key] = true;
        });
        setPermissions(defaultPermissions);
      }
    } catch (err) {
      setError('Failed to load permissions. Please refresh the page.');
      // Default to all enabled on error
      const defaultPermissions = {};
      features.forEach(feature => {
        defaultPermissions[feature.key] = true;
      });
      liveDataFeatures.forEach(feature => {
        defaultPermissions[feature.key] = true;
      });
      setPermissions(defaultPermissions);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (featureKey) => {
    setPermissions(prev => ({
      ...prev,
      [featureKey]: !prev[featureKey]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`/api/admin/users/${user.username}/permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ permissions }),
      });

      const data = await response.json();
      
      if (data.success) {
        alert('Permissions saved successfully!');
        onBack();
      } else {
        setError(data.error || 'Failed to save permissions');
      }
    } catch (err) {
      setError('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer>
      <HeaderBar>
        <BackButton onClick={onBack}>
          <FiArrowLeft size={16} />
          BACK
        </BackButton>
        <PortalTitle>
          TimeWatt
          <LightningBoltIcon src="/lightning-bolt.png" alt="Lightning Bolt" />
        </PortalTitle>
        <div></div>
      </HeaderBar>
      <MainContent>
        <ContentWrapper>
          <PageHeader>
            <PageTitle>Set Access Permissions - {user.username}</PageTitle>
          </PageHeader>

          {loading ? (
            <LoadingProgress />
          ) : error && !loading ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : (
            <>
              <SectionsContainer>
                <AccessSection>
                  <SectionTitle>Feature Access</SectionTitle>
                  <FeatureList>
                    {features.map((feature) => (
                      <FeatureItem key={feature.key} enabled={permissions[feature.key]}>
                        <FeatureInfo>
                          <FeatureName>{feature.name}</FeatureName>
                          <FeatureDescription>{feature.description}</FeatureDescription>
                        </FeatureInfo>
                        <ToggleSwitch>
                          <ToggleInput
                            type="checkbox"
                            checked={permissions[feature.key] || false}
                            onChange={() => handleToggle(feature.key)}
                            disabled={saving}
                          />
                          <ToggleSlider />
                        </ToggleSwitch>
                      </FeatureItem>
                    ))}
                  </FeatureList>
                </AccessSection>

                <AccessSection>
                  <SectionTitle>Live Page Data Visibility</SectionTitle>
                  <FeatureList>
                    {liveDataFeatures.map((feature) => (
                      <FeatureItem key={feature.key} enabled={permissions[feature.key]}>
                        <FeatureInfo>
                          <FeatureName>{feature.name}</FeatureName>
                          <FeatureDescription>{feature.description}</FeatureDescription>
                        </FeatureInfo>
                        <ToggleSwitch>
                          <ToggleInput
                            type="checkbox"
                            checked={permissions[feature.key] !== false}
                            onChange={() => handleToggle(feature.key)}
                            disabled={saving}
                          />
                          <ToggleSlider />
                        </ToggleSwitch>
                      </FeatureItem>
                    ))}
                  </FeatureList>
                </AccessSection>
              </SectionsContainer>

              <SaveButton onClick={handleSave} disabled={saving}>
                <FiCheck size={18} />
                {saving ? 'Saving...' : 'Save Permissions'}
              </SaveButton>
            </>
          )}
        </ContentWrapper>
      </MainContent>
    </PageContainer>
  );
};

export default SetAccessPage;

